import { useMemo } from "react";

import {
    TokenRestrictionCode,
    TokenRestrictedActions,
    useTokenRestrictionsContext,
    RestrictionExpanded
} from "@bridgesplit/abf-react";
import { filterTruthy } from "@bridgesplit/utils";

export interface RestrictionWithMessage extends RestrictionExpanded {
    formattedMessage: string;
    ctaLabel?: string;
}

export interface TokenRestrictionsConfig {
    restrictions: RestrictionWithMessage[] | undefined;
    activeRestrictionCta: string | undefined;
    activeRestrictionCodes: TokenRestrictionCode[] | undefined;
    isBlocking: boolean;
}

interface ErrorCodeConfig {
    ctaLabel: string;
    link?: string;
}

// Action names map
const ACTION_NAMES: Record<TokenRestrictedActions, string> = {
    [TokenRestrictedActions.All]: "All actions",
    [TokenRestrictedActions.Borrow]: "Borrow",
    [TokenRestrictedActions.Repay]: "Repay",
    [TokenRestrictedActions.Lend]: "Lend",
    [TokenRestrictedActions.LoopStart]: "Start Loop",
    [TokenRestrictedActions.LoopClose]: "Close Loop",
    [TokenRestrictedActions.WithdrawCollateral]: "Withdraw Collateral"
};

const ERROR_CODE_CONFIG: Record<TokenRestrictionCode, ErrorCodeConfig> = {
    [TokenRestrictionCode.UsMarketHours]: {
        ctaLabel: "Market Closed",
        link: "https://docs.loopscale.com/using-loopscale/loop#market-hours"
    },
    [TokenRestrictionCode.UsFxMarketHours]: {
        ctaLabel: "Market Closed",
        link: "https://docs.loopscale.com/using-loopscale/loop#market-hours"
    },
    [TokenRestrictionCode.AssetPool]: {
        ctaLabel: "Pool Limited"
    },
    [TokenRestrictionCode.GeneralDisabled]: {
        ctaLabel: "Disabled"
    },
    [TokenRestrictionCode.CountryRestricted]: {
        ctaLabel: "Country Restricted"
    }
};

// Error message templates (for blocking restrictions)
const ERROR_MESSAGES = {
    [TokenRestrictionCode.UsMarketHours]: (prefix: string, symbol: string | undefined) =>
        `${prefix} functionality for ${symbol} is unavailable outside of market hours.`,
    [TokenRestrictionCode.UsFxMarketHours]: (prefix: string, symbol: string | undefined) =>
        `${prefix} functionality for ${symbol} is limited outside of market hours.`,
    [TokenRestrictionCode.AssetPool]: (prefix: string, symbol: string | undefined) =>
        `${prefix} functionality for ${symbol} is restricted due to complex mint/redemption mechanics.`,
    [TokenRestrictionCode.GeneralDisabled]: (prefix: string, symbol: string | undefined) =>
        `${prefix} functionality for ${symbol} is currently disabled.`,
    [TokenRestrictionCode.CountryRestricted]: (prefix: string, symbol: string | undefined) =>
        `${symbol} is not available in your country.`
};

// Warning message templates (currently duplicates - customize later)
const WARNING_MESSAGES = {
    [TokenRestrictionCode.UsMarketHours]: (prefix: string, symbol: string | undefined) =>
        `${prefix} functionality for ${symbol} is limited when outside of market hours.`,
    [TokenRestrictionCode.UsFxMarketHours]: (prefix: string, symbol: string | undefined) =>
        `${prefix} functionality for ${symbol} is limited when outside of market hours.`,
    [TokenRestrictionCode.AssetPool]: (prefix: string, symbol: string | undefined) =>
        `${prefix} functionality for ${symbol} can be restricted due to complex mint/redemption mechanics.`,
    [TokenRestrictionCode.GeneralDisabled]: (prefix: string, symbol: string | undefined) =>
        `${prefix} functionality for ${symbol} is currently disabled.`,
    [TokenRestrictionCode.CountryRestricted]: (prefix: string, symbol: string | undefined) =>
        `${symbol} is not available in your country.`
};

// Helper to convert action enum to readable name
function getActionName(action: TokenRestrictedActions): string {
    return ACTION_NAMES[action] ?? action;
}

// Helper to format multiple actions grammatically
function formatActionLabel(actions: TokenRestrictedActions[]): string {
    const actionNames = actions.map(getActionName);

    if (actionNames.length === 1) {
        return actionNames[0];
    } else if (actionNames.length === 2) {
        return `${actionNames[0]} and ${actionNames[1]}`;
    } else {
        // 3 or more: use Oxford comma
        const allButLast = actionNames.slice(0, -1).join(", ");
        const last = actionNames[actionNames.length - 1];
        return `${allButLast}, and ${last}`;
    }
}

// Get default message with separate configs for blocking and warning
function getDefaultMessage(
    code: TokenRestrictionCode,
    actionLabel: string,
    tokenSymbol: string | undefined,
    isBlocking: boolean,
    ignoreActionText: boolean
): string {
    const prefix = ignoreActionText ? "Functionality" : actionLabel;

    // Use error messages if blocking, otherwise use warning messages
    if (isBlocking || ignoreActionText) {
        const messageFn = ERROR_MESSAGES[code];
        return messageFn ? messageFn(prefix, tokenSymbol) : `${prefix} for ${tokenSymbol} is currently restricted.`;
    } else {
        const messageFn = WARNING_MESSAGES[code];
        return messageFn ? messageFn(prefix, tokenSymbol) : `${prefix} for ${tokenSymbol} may be restricted.`;
    }
}

// Format restriction message based on blocking state
function formatRestrictionMessage(
    restriction: RestrictionExpanded,
    tokenSymbol: string | undefined,
    isBlocking: boolean,
    includeActions: boolean
): string {
    const actionLabel = formatActionLabel(restriction.restrictedActions);

    const message = getDefaultMessage(
        restriction.restrictionCode,
        actionLabel,
        tokenSymbol,
        isBlocking,
        includeActions
    );

    return message;
}

export function useTokenActionRestrictionsConfig({
    mints,
    primaryRelevantAction,
    secondaryRelevantAction,
    isActive,
    restrictionCodes,
    ignoreActionText = false
}: {
    mints: string[] | undefined;
    primaryRelevantAction?: TokenRestrictedActions;
    secondaryRelevantAction?: TokenRestrictedActions;
    isActive?: boolean;
    restrictionCodes?: TokenRestrictionCode[];
    ignoreActionText?: boolean;
}): TokenRestrictionsConfig | undefined {
    const { getTokenRestrictionsForMints } = useTokenRestrictionsContext();

    const restrictions = getTokenRestrictionsForMints({
        mints,
        relevantActions: [primaryRelevantAction, secondaryRelevantAction].filter(filterTruthy),
        isActive,
        restrictionCodes
    });

    return useMemo(() => {
        if (!restrictions) return undefined;

        // Filter blocking restrictions (primary action + active)
        const blocking = primaryRelevantAction
            ? restrictions.filter(
                  (r) =>
                      r.isActive &&
                      (r.restrictedActions.includes(TokenRestrictedActions.All) ||
                          r.restrictedActions.includes(primaryRelevantAction))
              )
            : [];

        const isRestricted = blocking.length > 0;

        // Return blocking if they exist, otherwise return all restrictions (warnings)
        const displayRestrictions = isRestricted ? blocking : restrictions;

        // Attach formatted messages to each restriction
        const restrictionsWithMessages: RestrictionWithMessage[] = displayRestrictions.map((restriction) => {
            const tokenSymbol = restriction.token?.symbol;

            // Format the message and effected actions text
            const formatted = formatRestrictionMessage(restriction, tokenSymbol, isRestricted, ignoreActionText);

            // Prioritize custom messages
            const message = isRestricted
                ? restriction.customErrorMessage || formatted
                : restriction.customWarningMessage || formatted;

            // Get default label and link from config
            const errorCodeConfig = ERROR_CODE_CONFIG[restriction.restrictionCode];

            return {
                ...restriction,
                formattedMessage: message,
                ctaLabel: errorCodeConfig?.ctaLabel,
                link: restriction.link || errorCodeConfig?.link
            };
        });

        // Concatenate unique CTAs for blocking restrictions only
        const activeRestrictionCta = isRestricted
            ? Array.from(new Set(restrictionsWithMessages.map((r) => r.ctaLabel).filter(filterTruthy))).join(" & ") ||
              undefined
            : undefined;

        const activeRestrictionCodes = displayRestrictions.map((r) => r.restrictionCode);

        return {
            restrictions: restrictionsWithMessages.length > 0 ? restrictionsWithMessages : undefined,
            activeRestrictionCta,
            isBlocking: isRestricted,
            activeRestrictionCodes: activeRestrictionCodes.length > 0 ? activeRestrictionCodes : undefined
        };
    }, [restrictions, primaryRelevantAction, ignoreActionText]);
}
